import React, { useState, useEffect, useCallback } from 'react'
import api from '../services/api'
import { useIsMobile } from '../hooks/useIsMobile'
import toast from 'react-hot-toast'
import {
  Search, Eye, RefreshCw, ChevronLeft, ChevronRight,
  Mail, CheckCircle2, XCircle, Clock, AlertTriangle,
  ExternalLink, MousePointerClick, FileText, Send, Sparkles
} from 'lucide-react'
import { format, parseISO } from 'date-fns'

function fmtDate(val) {
  if (!val) return '—'
  try { return format(parseISO(val), 'dd/MM/yy HH:mm:ss') } catch { return val }
}

// ── Email Status Badge ────────────────────────────────────────────────────────
function EmailStatusBadge({ status, skipReason }) {
  // status: null/undefined = pending, 'success' = ok, 'failed' = error, 'skipped' = not eligible
  const variants = {
    success: { label: '✅ EMAIL SENT', bg: 'var(--green-bg, #DCFCE7)', color: 'var(--green, #166534)', border: '#BBF7D0' },
    failed:  { label: '❌ FAILED',     bg: 'var(--red-bg, #FEE2E2)', color: 'var(--red, #991B1B)', border: '#FECACA' },
    skipped: { label: '⏭ NOT SENT',   bg: 'var(--amber-bg, #FEF3C7)', color: 'var(--amber, #92400E)', border: '#FDE68A' },
    pending: { label: '⏳ QUEUED',    bg: 'var(--blue-bg, #E0F2FE)', color: 'var(--blue, #0369A1)', border: '#BAE6FD' },
  }
  const v = variants[status] || variants.pending
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
        background: v.bg, color: v.color, border: `1px solid ${v.border}`,
        borderRadius: 999, fontWeight: 700, fontSize: 11, letterSpacing: 0.3,
      }}>
        {v.label}
      </span>
      {status === 'skipped' && skipReason && (
        <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {skipReason}
        </span>
      )}
    </div>
  )
}

function UseCaseBadge({ value }) {
  const isExternal = value === 'external_provider'
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 6,
      background: isExternal ? 'var(--purple-bg, #F3E8FF)' : 'var(--blue-bg, #DBEAFE)',
      color: isExternal ? 'var(--purple, #6B21A8)' : 'var(--blue, #1E40AF)',
      fontSize: 11, fontWeight: 600,
    }}>
      {isExternal ? 'Renovo S2S' : 'Remarketing'}
    </span>
  )
}

// ── Mobile Lead Card ──────────────────────────────────────────────────────────
function LeadCard({ row, onView }) {
  const lead = row.lead_summary || {}
  const last = row.last_post || {}
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || '—'

  return (
    <div className="card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Enqueued + Lead ID + View */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {fmtDate(row.enqueued_at || row.createdAt)}
          </div>
          <div style={{
            fontFamily: 'monospace', fontSize: 12, fontWeight: 500,
            wordBreak: 'break-all', marginTop: 2,
          }}>
            {row.kickoff_lead_id || '—'}
          </div>
        </div>
        <button
          className="btn btn-ghost btn-sm btn-icon"
          title="View detail"
          onClick={onView}
          style={{ flexShrink: 0 }}
        >
          <Eye size={14} />
        </button>
      </div>

      {/* Lead Info */}
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
          <Mail size={10} /> {lead.email || '—'}
          {lead.state && <span>· {lead.state}</span>}
        </div>
      </div>

      {/* Use Case & Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <UseCaseBadge value={row.use_case} />
        <EmailStatusBadge status={row.posted_to_hyalite} skipReason={row.skip_reason} />
      </div>
      {last.posted_at && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
          {fmtDate(last.posted_at)} · HTTP {last.response_status}
        </div>
      )}

      {/* Engagement Return */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 3,
        paddingTop: 8, borderTop: '1px solid var(--border)',
      }}>
        <span style={{
          fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5,
          color: 'var(--text-muted)', fontWeight: 600,
        }}>
          Return
        </span>
        {last.click_received_at ? (
          <span style={{ fontSize: 11, color: 'var(--purple)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <MousePointerClick size={11} /> Click {fmtDate(last.click_received_at)}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No click</span>
        )}
        {last.form_captured_at && (
          <span style={{ fontSize: 11, color: 'var(--red)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <FileText size={11} /> Form {fmtDate(last.form_captured_at)}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
function DetailModal({ leadId, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.get(`/hyalite/leads/${encodeURIComponent(leadId)}`)
      .then(({ data }) => { if (!cancelled) setData(data) })
      .catch(() => toast.error('Could not load detail'))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [leadId])

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 1000, width: '92%' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
            <Mail size={18} /> Email Reengagement detail — <code style={{ fontSize: 13, wordBreak: 'break-all' }}>{leadId}</code>
          </h3>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ flexDirection: 'column', gap: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 30 }}><div className="spinner" /></div>
          ) : !data ? (
            <p style={{ color: 'var(--text-muted)' }}>No data</p>
          ) : (
            <>
              {/* Queue Status */}
              <div>
                <h4 style={{ margin: '0 0 8px', color: 'var(--text-secondary)' }}>Current Status</h4>
                <div style={{ background: 'var(--bg-base)', padding: 12, borderRadius: 8, fontSize: 13 }}>
                  <div><strong>Use case:</strong> {data.queue_item?.use_case}</div>
                  <div><strong>Status:</strong> {data.queue_item?.posted_to_hyalite || 'pending'}</div>
                  <div><strong>Skip reason:</strong> {data.queue_item?.skip_reason || '—'}</div>
                  <div><strong>Attempts:</strong> {data.queue_item?.attempt_count ?? 0}</div>
                  <div><strong>Queued:</strong> {fmtDate(data.queue_item?.enqueued_at)}</div>
                  <div><strong>Eligible since:</strong> {fmtDate(data.queue_item?.eligible_at)}</div>
                </div>
              </div>

              {/* Delivery Dispatches */}
              <div>
                <h4 style={{ margin: '0 0 8px', color: 'var(--text-secondary)' }}>CampaignKit Dispatches ({data.posts?.length || 0})</h4>
                {(!data.posts || data.posts.length === 0) ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Not yet queued for email remarketing.</p>
                ) : data.posts.map((p) => (
                  <div key={p._id} style={{ background: '#1e1e1e', color: '#d4d4d4', padding: 12, borderRadius: 8, marginBottom: 8, fontSize: 12, fontFamily: 'monospace' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
                      <span>📅 {fmtDate(p.posted_at)}</span>
                      <span style={{ color: p.response_ok ? '#86efac' : '#fca5a5' }}>
                        HTTP {p.response_status} {p.response_ok ? '✅' : '❌'}
                      </span>
                    </div>
                    <details>
                      <summary style={{ cursor: 'pointer', color: '#93c5fd' }}>View payload + response</summary>
                      <div style={{ marginTop: 8 }}>
                        <strong>Payload:</strong>
                        <pre style={{ margin: 4, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(p.payload, null, 2)}</pre>
                        <strong>Response:</strong>
                        <pre style={{ margin: 4, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(p.response_body, null, 2)}</pre>
                      </div>
                    </details>
                  </div>
                ))}
              </div>

              {/* Frontend Captures */}
              <div>
                <h4 style={{ margin: '0 0 8px', color: 'var(--text-secondary)' }}>Form Captures ({data.form_captures?.length || 0})</h4>
                {(!data.form_captures || data.form_captures.length === 0) ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No form captures yet.</p>
                ) : data.form_captures.map((c) => (
                  <div key={c._id} style={{ background: 'var(--bg-base)', padding: 10, borderRadius: 8, marginBottom: 6, fontSize: 12 }}>
                    <div><strong>{fmtDate(c.createdAt)}</strong> — method: <code>{c.capture_method}</code></div>
                    <details>
                      <summary style={{ cursor: 'pointer', color: 'var(--accent)' }}>View captured data</summary>
                      <pre style={{ margin: 4, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(c.captured_data, null, 2)}</pre>
                    </details>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

// ── Main Page Component ───────────────────────────────────────────────────────
export default function EmailReengagementPage() {
  const isMobile = useIsMobile()
  const [data, setData] = useState([])
  const [stats, setStats] = useState(null)
  const [total, setTotal] = useState(0)
  const [pages, setPages] = useState(1)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [useCaseFilter, setUseCaseFilter] = useState('')
  const [loading, setLoading] = useState(false)
  const [detailLeadId, setDetailLeadId] = useState(null)

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/hyalite/stats')
      setStats(data)
    } catch (err) { /* silent */ }
  }, [])

  const fetchData = useCallback(async (pg = 1, search = '', st = '', uc = '') => {
    setLoading(true)
    try {
      const params = { page: pg, limit: 20 }
      if (search.trim()) params.q = search.trim()
      if (st) params.status = st
      if (uc) params.use_case = uc
      const { data } = await api.get('/hyalite/leads', { params })
      setData(data.data || [])
      setTotal(data.total || 0)
      setPages(data.pages || 1)
      setPage(pg)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading email reengagement leads')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
    fetchData(1, '', '', '')
  }, [fetchData, fetchStats])

  const refresh = () => {
    fetchStats()
    fetchData(page, q, statusFilter, useCaseFilter)
  }

  // ── KPIs ───────────────────────────────────────────────────────────
  const kpis = [
    {
      label: 'Queued', value: stats?.by_status?.pending ?? 0,
      icon: Clock, bg: 'var(--blue-bg, #E0F2FE)', color: 'var(--blue, #0369A1)',
    },
    {
      label: 'Sent', value: stats?.by_status?.success ?? 0,
      icon: Send, bg: 'var(--green-bg, #DCFCE7)', color: 'var(--green, #166534)',
    },
    {
      label: 'Not Eligible', value: stats?.by_status?.skipped ?? 0,
      icon: AlertTriangle, bg: 'var(--amber-bg, #FEF3C7)', color: 'var(--amber, #92400E)',
    },
    {
      label: 'Email Clicks', value: stats?.total_clicks ?? 0,
      icon: MousePointerClick, bg: 'var(--purple-bg, #F3E8FF)', color: 'var(--purple, #6B21A8)',
    },
    {
      label: 'Forms Captured', value: stats?.total_form_captured ?? 0,
      icon: FileText, bg: 'var(--red-bg, #FCE7F3)', color: 'var(--red, #BE185D)',
    },
  ]

  // Pagination Element
  const paginationEl = (
    <div className="pagination" style={{ flexWrap: 'wrap', rowGap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 8 }}>
        Page {page} of {pages} · {total.toLocaleString()} records
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => fetchData(page - 1, q, statusFilter, useCaseFilter)} aria-label="Previous page">
        <ChevronLeft size={14} />
      </button>
      {Array.from({ length: Math.min(5, pages) }, (_, i) => {
        const p = Math.max(1, Math.min(pages - 4, page - 2)) + i
        return (
          <button
            key={p}
            className={`page-btn ${p === page ? 'active' : ''}`}
            onClick={() => fetchData(p, q, statusFilter, useCaseFilter)}
            aria-label={`Page ${p}`}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        )
      })}
      <button className="page-btn" disabled={page >= pages} onClick={() => fetchData(page + 1, q, statusFilter, useCaseFilter)} aria-label="Next page">
        <ChevronRight size={14} />
      </button>
    </div>
  )

  return (
    <div className="fade-in page-shell">
      <section className="page-hero compact-hero">
        <div className="page-hero-content">
          <span className="hero-kicker">Remarketing & Lifecycle</span>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Mail size={28} /> Email Remarketing
          </h1>
          <p>Email monetization & remarketing layer for un-sold or rejected leads. Tracks dispatches to CampaignKit and user clicks.</p>
          <div className="hero-chips">
            <span className="badge badge-blue">Total Queue: {(stats?.total_queue ?? 0).toLocaleString()}</span>
            <span className="badge badge-blue">Dispatched: {(stats?.total_posts ?? 0).toLocaleString()}</span>
            {stats?.total_unsubscribed != null && (
              <span className="badge">Unsubs: {stats.total_unsubscribed.toLocaleString()}</span>
            )}
          </div>
        </div>
      </section>

      {/* ── KPIs Grid ────────────────────────────────────────────── */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12, marginBottom: 16,
      }}>
        {kpis.map((k, i) => (
          <div key={i} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10, background: k.bg, color: k.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <k.icon size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {k.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
                {Number(k.value).toLocaleString()}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* ── Filters ──────────────────────────────────────────────── */}
      <section className="card section-card toolbar-card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <form
          onSubmit={(e) => { e.preventDefault(); fetchData(1, q, statusFilter, useCaseFilter) }}
          style={{ flex: 1, minWidth: isMobile ? '100%' : 240 }}
        >
          <div className="search-bar search-bar-elevated">
            <Search size={14} className="search-icon" />
            <input
              className="input"
              placeholder="Search by lead_id, email or use_case…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </form>

        <select
          className="input"
          style={{ width: isMobile ? '100%' : 160 }}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); fetchData(1, q, e.target.value, useCaseFilter) }}
        >
          <option value="">All Statuses</option>
          <option value="pending">⏳ Queued</option>
          <option value="success">✅ Sent</option>
          <option value="failed">❌ Failed</option>
          <option value="skipped">⏭ Not Eligible</option>
        </select>

        <select
          className="input"
          style={{ width: isMobile ? '100%' : 200 }}
          value={useCaseFilter}
          onChange={(e) => { setUseCaseFilter(e.target.value); fetchData(1, q, statusFilter, e.target.value) }}
        >
          <option value="">All Use Cases</option>
          <option value="kickoff_reengagement">Remarketing (Case A)</option>
          <option value="external_provider">Renovo S2S (Case B)</option>
        </select>

        <button
          className="btn btn-ghost"
          onClick={refresh}
          disabled={loading}
          style={{ width: isMobile ? '100%' : undefined, justifyContent: isMobile ? 'center' : undefined }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 0.7s linear infinite' : 'none' }} />
          Refresh
        </button>
      </section>

      {/* ── Data List / Cards ─────────────────────────────────────── */}
      {loading && data.length === 0 ? (
        <div className="page-shell-center"><div className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} /></div>
      ) : data.length === 0 ? (
        <div className="card empty-state section-card">
          <p>No leads in the email reengagement queue with the current filters.</p>
        </div>
      ) : isMobile ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.map((row) => (
              <LeadCard
                key={row._id}
                row={row}
                onView={() => setDetailLeadId(row.kickoff_lead_id)}
              />
            ))}
          </div>
          {paginationEl}
        </>
      ) : (
        <section className="card section-card table-card-shell">
          <div className="table-wrap table-wrap-elevated">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Queued</th>
                  <th>Lead ID</th>
                  <th>Lead Contact</th>
                  <th>Use Case</th>
                  <th>Email Delivery</th>
                  <th>Engagement Return</th>
                  <th style={{ width: 80 }}></th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => {
                  const lead = row.lead_summary || {}
                  const last = row.last_post || {}
                  return (
                    <tr key={row._id}>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {fmtDate(row.enqueued_at || row.createdAt)}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>
                        {row.kickoff_lead_id || '—'}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || '—'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Mail size={10} /> {lead.email || '—'}
                          {lead.state && <span style={{ marginLeft: 6 }}>· {lead.state}</span>}
                        </div>
                      </td>
                      <td><UseCaseBadge value={row.use_case} /></td>
                      <td>
                        <EmailStatusBadge
                          status={row.posted_to_hyalite}
                          skipReason={row.skip_reason}
                        />
                        {last.posted_at && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                            {fmtDate(last.posted_at)} · HTTP {last.response_status}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {last.click_received_at ? (
                            <span style={{ fontSize: 11, color: 'var(--purple)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <MousePointerClick size={11} /> Click {fmtDate(last.click_received_at)}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No click</span>
                          )}
                          {last.form_captured_at && (
                            <span style={{ fontSize: 11, color: 'var(--red)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <FileText size={11} /> Form {fmtDate(last.form_captured_at)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm btn-icon"
                          title="View detail"
                          onClick={() => setDetailLeadId(row.kickoff_lead_id)}
                        >
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {paginationEl}
        </section>
      )}

      {detailLeadId && (
        <DetailModal
          leadId={detailLeadId}
          onClose={() => setDetailLeadId(null)}
        />
      )}
    </div>
  )
}
